import React, { useState, useEffect } from 'react';
import { useTranslation } from 'utils/i18n';

// Import extracted logic
import { useNewsletterForm } from './logic/ui';
import { subscribeNewsletter, loadAllNewsletters } from './logic/api';
import translations from './translations';

/**
 * Newsletter Component
 * Displays newsletter subscription form and newsletter archive
 */
const Newsletter = () => {
  const { language } = useTranslation();
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load newsletters on mount and when language changes
  useEffect(() => {
    const fetchNewsletters = async () => {
      setLoading(true);
      try {
        const result = await loadAllNewsletters(language);
        if (result.success) {
          setNewsletters(result.data || []);
        }
      } catch (error) {
        console.error('Error loading newsletters:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNewsletters();
  }, [language]);

  // Get page-specific translations
  const t = (key, defaultValue = '') => {
    const pageTranslations = translations[language] || translations.en;
    const value = key.split('.').reduce((obj, k) => obj?.[k], pageTranslations);
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
    return defaultValue !== '' ? defaultValue : key;
  };

  // Handle form submission
  const handleFormSubmit = async (subscriptionData) => {
    try {
      const result = await subscribeNewsletter(subscriptionData);
      if (result.success) {
        alert(t('form.success', 'Thank you for subscribing to our newsletter!'));
      } else {
        alert(t('form.error', 'Failed to subscribe. Please try again.'));
      }
    } catch (error) {
      alert(t('form.error', 'Failed to subscribe. Please try again.'));
    }
  };

  const { subscriptionData, handleInputChange, handleSubmit, isSubmitting } = useNewsletterForm(handleFormSubmit);

  // Format date
  const formatDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="static-page">
      <div className="static-page-header">
        <div className="static-page-header-content">
          <div className="page-title-section">
            <h1>{t('title', 'Newsletter')}</h1>
            <p className="lead">
              {t('subtitle', 'Subscribe to our newsletter for the latest updates')}
            </p>
          </div>
        </div>
      </div>

      <div className="static-page-content">
        <section className="content-section">
          <h2>{t('form.subscribe', 'Subscribe to Newsletter')}</h2>
          <form onSubmit={handleSubmit} className="newsletter-form">
            <div className="form-group">
              <label htmlFor="email">{t('form.email', 'Email')} *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={subscriptionData.email}
                onChange={handleInputChange}
                required
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="firstName">{t('form.firstName', 'First Name')}</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={subscriptionData.firstName}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">{t('form.lastName', 'Last Name')}</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={subscriptionData.lastName}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="frequency">{t('form.frequency', 'Frequency')}</label>
              <select
                id="frequency"
                name="frequency"
                value={subscriptionData.frequency}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="weekly">{t('form.frequencyWeekly', 'Weekly')}</option>
                <option value="monthly">{t('form.frequencyMonthly', 'Monthly')}</option>
              </select>
            </div>

            <div className="form-group">
              <label>{t('form.interests', 'Interests')}</label>
              <div className="checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="interests.productUpdates"
                    checked={subscriptionData.interests.productUpdates}
                    onChange={handleInputChange}
                  />
                  {t('form.interestsProduct', 'Product Updates')}
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="interests.technicalInsights"
                    checked={subscriptionData.interests.technicalInsights}
                    onChange={handleInputChange}
                  />
                  {t('form.interestsTechnical', 'Technical Insights')}
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="interests.industryNews"
                    checked={subscriptionData.interests.industryNews}
                    onChange={handleInputChange}
                  />
                  {t('form.interestsIndustry', 'Industry News')}
                </label>
              </div>
            </div>

            <button type="submit" disabled={isSubmitting} className="submit-button btn btn-primary">
              {isSubmitting ? t('form.submitting', 'Subscribing...') : t('form.submit', 'Subscribe')}
            </button>
          </form>
        </section>

        {newsletters.length > 0 && (
          <section className="content-section">
            <h2>{t('archive.title', 'Newsletter Archive')}</h2>
            {loading ? (
              <p>{t('loading', 'Loading newsletters...')}</p>
            ) : (
              <div className="newsletter-archive">
                {newsletters.map((newsletter) => (
                  <article key={newsletter.slug} className="newsletter-item">
                    <div className="newsletter-meta">
                      <span className="newsletter-date">{formatDate(newsletter.date)}</span>
                      {newsletter.subject && (
                        <span className="newsletter-subject">{newsletter.subject}</span>
                      )}
                    </div>
                    <h3>{newsletter.title}</h3>
                    {newsletter.excerpt && <p>{newsletter.excerpt}</p>}
                    <a href={`/newsletter/${newsletter.slug}`} className="read-more">
                      {t('readMore', 'Read More')} →
                    </a>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default Newsletter;


